// ─────────────────────────────────────────────
//  NextRoomModal — route the room's current team to its next room.
//  Opened from the card's ➜ button (mirrors the Relief modal). Pick a
//  destination from the other rooms on the board; saving persists
//  nextRoomId / nextRoomName on the day's assignment row. The destination
//  card derives its own "incoming" arrow from that row (domain/nextRoom),
//  so the link is bidirectional with a single write.
// ─────────────────────────────────────────────
import React, { useState } from "react";
import tokens from "../theme/tokens";
import { useToast } from "../context/ToastContext";
import { nextRoomChoiceGroups, teamLabel } from "../domain/nextRoom";
import { saveNextRoom } from "../features/next-room/saveNextRoom";
import Modal from "./Modal";
import Button from "./Button";
import styles from "./roomCard.styles";

const { color } = tokens;

export default function NextRoomModal({ isOpen, onClose, room, assignment, rooms, assignments = [] }) {
  const toast = useToast();
  const [destId, setDestId] = useState(assignment?.nextRoomId || "");
  const [saving, setSaving] = useState(false);

  // Two sections: empty rooms to move into, scheduled rooms to take over.
  const groups = nextRoomChoiceGroups(rooms, room.id, assignments);
  const choices = [...groups.available, ...groups.scheduled];
  const team = teamLabel(assignment);

  const save = async () => {
    if (!assignment?.id) {
      toast.error("Assign the room before setting a next room.");
      return;
    }
    setSaving(true);
    try {
      const { destName, staged } = await saveNextRoom({
        assignment,
        room,
        destId,
        choices,
        assignments,
      });

      toast.success(
        destId
          ? staged
            ? `${destName} opened for the ${room.name} team.`
            : `${room.name} team heads to ${destName} next.`
          : `Next room cleared for ${room.name}.`
      );
      onClose();
    } catch (err) {
      console.error("Save next room failed:", err);
      toast.error("Could not save the next room. Check your connection.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={`Next room — ${room.name}`}
      maxWidth={400}
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button onClick={save} disabled={saving || !assignment?.id}>
            {saving ? "Saving…" : "Save next room"}
          </Button>
        </>
      }
    >
      <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
        <div style={{ fontSize: 12, color: color.text.secondary }}>
          Team — <strong>{team || "—"}</strong>
        </div>
        <label style={styles.reliefLabel}>
          Where does this team go next?
          <select
            value={destId}
            onChange={(e) => setDestId(e.target.value)}
            style={styles.reliefSelect}
          >
            <option value="">— none —</option>
            <optgroup label="Available rooms (empty)">
              {groups.available.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </optgroup>
            <optgroup label="Scheduled rooms (take over)">
              {groups.scheduled.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                  {c.team ? ` — ${c.team}` : ""}
                </option>
              ))}
            </optgroup>
          </select>
        </label>
        <div style={{ fontSize: 11, color: color.text.muted }}>
          The destination card shows this team as incoming until the link is
          cleared or the team is reassigned.
        </div>
        {!assignment?.id && (
          <div style={{ fontSize: 11, color: color.accent.error }}>
            This room has no assignment yet — assign it first to set a next room.
          </div>
        )}
      </div>
    </Modal>
  );
}
